import { useState } from 'react'
import { Alert, Button, Checkbox, Input, message, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { copyText, locatorGenerate } from '@/api/logText'
import type { LocatorCandidate, LocatorResult } from '@/types'

const { TextArea } = Input
const { Text } = Typography
const MONO = { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }

/** 定位器生成器页签：贴 HTML 片段 → 候选 XPath/CSS 定位表达式（配合 UI 自动化模块使用） */
export default function LocatorTab() {
  const [html, setHtml] = useState('')
  const [hint, setHint] = useState('')
  const [useAi, setUseAi] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<LocatorResult | null>(null)

  /** 生成候选定位器 */
  const handleGenerate = async () => {
    if (!html.trim()) {
      message.warning('请粘贴 HTML 片段')
      return
    }
    setLoading(true)
    try {
      setResult(await locatorGenerate({ html, target_hint: hint, use_ai: useAi }))
    } finally {
      setLoading(false)
    }
  }

  const scoreColor = (s: number) => (s >= 90 ? 'green' : s >= 70 ? 'blue' : s >= 50 ? 'orange' : 'default')

  const columns: ColumnsType<LocatorCandidate> = [
    { title: '优先级', dataIndex: 'score', width: 80, align: 'center', render: (v: number) => <Tag color={scoreColor(v)}>{v}</Tag> },
    { title: '类型', dataIndex: 'kind', width: 80, align: 'center', render: (v: string) => <Tag color={v === 'css' ? 'cyan' : 'purple'}>{v.toUpperCase()}</Tag> },
    {
      title: '表达式', dataIndex: 'expr', ellipsis: true,
      render: (v: string, r: LocatorCandidate) => (
        <Space>
          <span style={MONO}>{v}</span>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => copyText(v)}>复制</Button>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.strategy}</Text>
        </Space>
      ),
    },
    { title: '目标元素', dataIndex: 'element', width: 220, ellipsis: true, render: (v: string) => <span style={MONO}>{v}</span> },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, padding: '0 22px 12px', overflowY: 'auto' }}>
      <Alert
        type="info"
        showIcon
        message="粘贴 HTML 片段生成候选定位器，优先使用 data-testid / 唯一 id / 表单组合，结构路径仅作兜底"
        style={{ flexShrink: 0 }}
      />
      <TextArea
        rows={8}
        placeholder="粘贴 HTML 片段（如浏览器 Elements 面板复制的元素）"
        value={html}
        onChange={(e) => setHtml(e.target.value)}
        style={MONO}
      />
      <Space wrap style={{ flexShrink: 0 }}>
        <Input
          placeholder="目标元素描述（可空），如：登录按钮 / 用户名输入框"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          style={{ width: 300 }}
        />
        <Checkbox checked={useAi} onChange={(e) => setUseAi(e.target.checked)}>附加 AI 推荐</Checkbox>
        <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }} loading={loading} onClick={handleGenerate}>
          生成定位器
        </Button>
      </Space>

      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
          {result.note && <Text type="secondary">{result.note}（共 {result.element_count ?? 0} 个元素）</Text>}
          {result.candidates.length > 0 && (
            <Table size="small" columns={columns} dataSource={result.candidates} rowKey={(_, i) => String(i)} pagination={{ pageSize: 10 }} />
          )}
          {result.ai_error && <Alert type="warning" showIcon message={`AI 推荐失败：${result.ai_error}`} />}
          {result.ai?.parsed && (
            <div>
              <Text strong>AI 推荐：</Text>
              <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(result.ai.recommended ?? []).map((r, i) => (
                  <div key={i}>
                    <Space wrap>
                      <Tag color={r.type === 'css' ? 'cyan' : 'purple'} style={MONO}>{r.expr}</Tag>
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => copyText(r.expr)}>复制</Button>
                    </Space>
                    <div><Text type="secondary">{r.reason}</Text></div>
                  </div>
                ))}
              </div>
              {result.ai.advice && <div style={{ marginTop: 4 }}><Text type="secondary">策略：{result.ai.advice}</Text></div>}
            </div>
          )}
          {result.ai && !result.ai.parsed && (
            <div>
              <Tag color="warning">AI 推荐返回格式未解析，原文：</Tag>
              <pre style={{ ...MONO, whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{result.ai.raw}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
